/**
 * Derives disconnect payload for Shippo rows on the integrations card.
 * Matches papaya `encodeShippoStoreKey` / `shippoIntegrationKeyId` and ShippoConnectionModalContent tag parsing.
 */
import {
  isShippoIntegrationKeyId,
  isShippoLegacyIntegrationKeyId,
  parseIntegrationTags,
} from './integrationTags'

export type ShippoDisconnectAccount = {
  _keyId?: string
  merchant_id?: string
  payhelm_store_channel?: string
}

/** Decode `{owner}--shippo--{base64url}` third segment → merchant_id + PayHelm store channel. */
export function decodeShippoStoreFromIntegrationKeyId(keyId: string): {
  merchant_id: string
  payhelm_channel: string
} | null {
  const parts = keyId.split('--')
  if (parts.length < 3 || parts[1] !== 'shippo') return null
  const segment = parts[2]
  try {
    let base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
    const padLen = (4 - (base64.length % 4)) % 4
    base64 += '='.repeat(padLen)
    const binary = atob(base64)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)
    const raw = new TextDecoder().decode(bytes)
    const pipe = raw.indexOf('|')
    if (pipe === -1) return null
    const merchant_id = raw.slice(0, pipe).trim()
    const payhelm_channel = raw.slice(pipe + 1).trim()
    if (!merchant_id || !payhelm_channel) return null
    return { merchant_id, payhelm_channel }
  } catch {
    return null
  }
}

export function resolveShippoDisconnectAccount(
  account: Record<string, unknown>,
  ppKeys?: Array<{ keyId?: string; tags?: unknown }>
): ShippoDisconnectAccount | null {
  const keyId = account._keyId != null ? String(account._keyId) : ''
  if (!keyId || !isShippoIntegrationKeyId(keyId)) return null

  if (isShippoLegacyIntegrationKeyId(keyId)) {
    return { _keyId: keyId }
  }

  let merchant_id = String(account.merchant_id ?? '').trim()
  let payhelm_store_channel = String(account.payhelm_store_channel ?? '').trim()

  if (!merchant_id || !payhelm_store_channel) {
    const row = ppKeys?.find((k) => k?.keyId === keyId)
    if (row?.tags) {
      const tm = parseIntegrationTags(row.tags)
      merchant_id = merchant_id || String(tm.merchant_id ?? '').trim()
      payhelm_store_channel = payhelm_store_channel || String(tm.payhelm_channel ?? '').trim()
    }
  }

  if (!merchant_id || !payhelm_store_channel) {
    const decoded = decodeShippoStoreFromIntegrationKeyId(keyId)
    if (decoded) {
      merchant_id = merchant_id || decoded.merchant_id
      payhelm_store_channel = payhelm_store_channel || decoded.payhelm_channel
    }
  }

  if (!merchant_id || !payhelm_store_channel) return null

  return { _keyId: keyId, merchant_id, payhelm_store_channel }
}
